// Клиенти (04 §8) — the list, and the client detail beside it.
//
// The cash strip at the top is labelled "каса", never "печалба". It is receivables and exposure:
// what has been collected, what is owed, and what the owner has paid a supplier for and not been
// reimbursed. Labour and own infrastructure are not in it (D42), so calling it profit would be a
// number that lies a little more every month.

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconClients } from '../../components/Icons.js';
import { ClientDetail } from './ClientDetail.js';
import { ConvertDialog } from './ConvertDialog.js';
import { cashView, useAllCharges, useClients, type ClientRow } from './useClients.js';
import './clients.css';

const STATUS_LABELS: Record<ClientRow['status'], string> = {
  active: 'активен',
  paused: 'на пауза',
  ended: 'приключен',
};

export function money(value: number): string {
  return new Intl.NumberFormat('bg-BG', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

/** Totals across every client, from the same `cashView` the detail screen uses. */
export function CashStrip() {
  const { data: charges = [] } = useAllCharges();
  const view = cashView(charges);

  return (
    <div className="cash-container">
      <dl className="cash">
        <div className="cash__card cash__card--collected">
          <dt>събрано</dt>
          <dd>{money(view.collected)}</dd>
          <span className="cash__sub">фактурирано & платено</span>
        </div>
        <div className="cash__card cash__card--outstanding">
          <dt>дължимо</dt>
          <dd>{money(view.outstanding)}</dd>
          <span className="cash__sub">очаквано плащане</span>
        </div>
        <div className={`cash__card ${view.overdue > 0 ? 'cash__card--late' : ''}`}>
          <dt>просрочено</dt>
          <dd>{money(view.overdue)}</dd>
          <span className="cash__sub">
            {view.overdue > 0 ? 'изисква напомняне' : 'няма закъснения'}
          </span>
        </div>
        <div className="cash__card cash__card--expense">
          <dt>от джоба</dt>
          <dd>{money(view.outOfPocket)}</dd>
          <span className="cash__sub">домейни и доставчици</span>
        </div>
      </dl>
      <p className="cash__note">
        Показателите отразяват паричен поток (вземания и изложен ресурс), не нетна счетоводна
        печалба (D42).
      </p>
    </div>
  );
}

export function ClientsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const selected = id === undefined ? null : Number(id);
  const { data: clients = [], isLoading } = useClients();
  const [converting, setConverting] = useState(false);
  const [search, setSearch] = useState('');

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => c.business_name.toLowerCase().includes(q));
  }, [clients, search]);

  return (
    <div className="clients">
      <aside className="clients__list">
        <div className="clients__head">
          <div className="clients__head-title">
            <h2>Клиенти</h2>
            <span className="clients__head-badge">{clients.length}</span>
          </div>
          <button type="button" className="clients__add-btn" onClick={() => setConverting(true)}>
            + Нов клиент от лийд
          </button>
          <input
            type="search"
            className="clients__search"
            placeholder="Търсене на клиент…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {isLoading ? <p className="clients__msg">Зареждане на клиенти…</p> : null}
        {!isLoading && clients.length === 0 ? (
          <div className="clients__empty-prompt">
            <p className="clients__msg">
              Още няма клиенти. Когато спечелите сделка в канбана, добавете клиент чрез бутона
              по-горе.
            </p>
          </div>
        ) : null}

        <div className="clients__items">
          {filteredClients.map((client) => {
            const isSelected = client.id === selected;
            return (
              <Link
                key={client.id}
                to={`/clients/${client.id}`}
                className={`clients__card ${isSelected ? 'clients__card--on' : ''}`}
              >
                <div className="clients__card-top">
                  <span className="clients__name">{client.business_name}</span>
                  <span className={`clients__status clients__status--${client.status}`}>
                    {STATUS_LABELS[client.status]}
                  </span>
                </div>
                <div className="clients__card-meta">
                  {client.package_key ? (
                    <span className="clients__pkg">{client.package_key}</span>
                  ) : null}
                  {client.monthly_fee_eur ? (
                    <span className="clients__fee">
                      {money(Number(client.monthly_fee_eur))}/мес.
                    </span>
                  ) : null}
                </div>
              </Link>
            );
          })}
          {filteredClients.length === 0 && clients.length > 0 ? (
            <p className="clients__msg">Няма намерени клиенти по това търсене.</p>
          ) : null}
        </div>
      </aside>

      <section className="clients__detail">
        {selected === null ? (
          <div className="clients__empty-view">
            <CashStrip />
            <div className="clients__select-prompt">
              <span className="clients__prompt-icon" aria-hidden="true">
                <IconClients size={28} />
              </span>
              <h3>Изберете клиент от списъка</h3>
              <p>Разгледайте активните начисления, статус на сайта и задачи по поддръжката.</p>
            </div>
          </div>
        ) : (
          <ClientDetail clientId={selected} />
        )}
      </section>

      {converting ? (
        <ConvertDialog
          onClose={() => setConverting(false)}
          onCreated={(clientId) => {
            setConverting(false);
            navigate(`/clients/${clientId}`);
          }}
        />
      ) : null}
    </div>
  );
}
